import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StorageSpace } from '@/api/console';
import {
    Button,
    DataTable,
    StatusTag,
    type ColumnConfig,
    type StatusConfig,
} from '@/components/ui';
import { formatBytes, usageShare } from './storageFormat';
import styles from './StorageSpaces.module.css';

interface StorageSpacesTableProps {
    spaces: StorageSpace[];
    pagination: { page: number; pageSize: number; total: number };
    loading: boolean;
    onPageChange: (page: number, pageSize: number) => void;
    /** Absent without `console.storage.manage` — the edit action is not rendered at all. */
    onEditQuota?: (space: StorageSpace) => void;
}

function UsageCell({ space }: { space: StorageSpace }) {
    const { t } = useTranslation();
    const share = usageShare(space.used_bytes, space.reserved_bytes, space.quota_bytes);
    const usedClass = share.totalPct >= 100 ? `${styles.barUsed} ${styles.isFull}` : styles.barUsed;
    return (
        <div className={styles.usage}>
            <div
                className={styles.bar}
                role="img"
                aria-label={t('console.storage.table.usageAria', {
                    used: formatBytes(space.used_bytes),
                    reserved: formatBytes(space.reserved_bytes),
                    quota: formatBytes(space.quota_bytes),
                })}
            >
                <span className={usedClass} style={{ width: `${share.usedPct}%` }} />
                <span className={styles.barReserved} style={{ width: `${share.reservedPct}%` }} />
            </div>
            <span className={styles.muted}>
                {t('console.storage.table.usageText', {
                    used: formatBytes(space.used_bytes),
                    quota: formatBytes(space.quota_bytes),
                })}
                {space.reserved_bytes > 0 &&
                    ` · ${t('console.storage.table.reservedText', {
                        reserved: formatBytes(space.reserved_bytes),
                    })}`}
            </span>
        </div>
    );
}

/**
 * ECR-85: the space list. One row per Storage space across every organisation;
 * the usage bar draws committed (`used_bytes`) and in-flight (`reserved_bytes`)
 * bytes against the EFFECTIVE quota.
 */
export const StorageSpacesTable = memo(function StorageSpacesTable({
    spaces,
    pagination,
    loading,
    onPageChange,
    onEditQuota,
}: StorageSpacesTableProps) {
    const { t } = useTranslation();

    const columns: ColumnConfig<StorageSpace>[] = useMemo(() => {
        const statusConfig: Record<string, StatusConfig> = {
            active: { label: t('console.storage.status.active'), tone: 'success' },
            read_only: { label: t('console.storage.status.read_only'), tone: 'warning' },
            suspended: { label: t('console.storage.status.suspended'), tone: 'error' },
            erased: { label: t('console.storage.status.erased'), tone: 'default' },
        };
        const quotaSourceConfig: Record<string, StatusConfig> = {
            override: { label: t('console.storage.quotaSource.override'), tone: 'processing' },
            default: { label: t('console.storage.quotaSource.default'), tone: 'default' },
        };
        return [
            {
                key: 'organization',
                title: t('console.storage.table.organization'),
                render: (_v, space) => (
                    <>
                        <span className={styles.orgName}>
                            {space.organization_name || t('console.storage.table.unnamedOrg')}
                        </span>
                        <span className={styles.muted}>{space.scope_id}</span>
                    </>
                ),
            },
            {
                key: 'app_code',
                title: t('console.storage.table.app'),
                dataIndex: 'app_code',
                render: (_v, space) => <code>{space.app_code}</code>,
            },
            {
                key: 'usage',
                title: t('console.storage.table.usage'),
                render: (_v, space) => <UsageCell space={space} />,
            },
            {
                key: 'file_count',
                title: t('console.storage.table.files'),
                align: 'right',
                render: (_v, space) => space.file_count.toLocaleString(),
            },
            {
                key: 'quota',
                title: t('console.storage.table.quota'),
                render: (_v, space) => (
                    <>
                        <span>{formatBytes(space.quota_bytes)}</span>
                        <StatusTag
                            status={space.quota_override_bytes === null ? 'default' : 'override'}
                            config={quotaSourceConfig}
                        />
                    </>
                ),
            },
            {
                key: 'status',
                title: t('console.storage.table.status'),
                render: (_v, space) => <StatusTag status={space.status} config={statusConfig} />,
            },
        ];
    }, [t]);

    return (
        <DataTable<StorageSpace>
            data={spaces}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={pagination}
            onPageChange={onPageChange}
            emptyText={t('console.storage.emptyNoResults')}
            actions={
                onEditQuota
                    ? (space) => (
                          <Button
                              size="small"
                              disabled={space.status === 'erased'}
                              onClick={() => onEditQuota(space)}
                              aria-label={t('console.storage.table.editAria', {
                                  name: space.organization_name || space.scope_id,
                              })}
                          >
                              {t('console.storage.table.editAction')}
                          </Button>
                      )
                    : undefined
            }
        />
    );
});
